import { useEffect, useRef, useState } from "react";
import {
  Constants,
  useMediaDevice,
  createCameraVideoTrack,
  createMicrophoneAudioTrack,
  getNetworkStats,
} from "@videosdk.live/react-sdk";
import { Tooltip } from "@mui/material";

import "../styles/precall.css";


export default function PreCallSetup({ getMeetingAndToken, meetingId, onStartMeeting }) {
  const videoRef = useRef();
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [webcams, setWebcams] = useState([]);
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedWebcam, setSelectedWebcam] = useState(null);
  const [selectedMic, setSelectedMic] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [micLevel, setMicLevel] = useState(0);
  const [speakerTesting, setSpeakerTesting] = useState(false);
  const [inputId, setInputId] = useState("");

  const { checkPermissions, getCameras, getMicrophones, getPlaybackDevices, requestPermission } = useMediaDevice();

  const getVideoTrack = async ({ webcamId }) => {
    try {
      const track = await createCameraVideoTrack({
        cameraId: webcamId,
        encoderConfig: "h540p_w960p",
        optimizationMode: "motion",
        multiStream: false,
      });
      return track.getVideoTracks()[0];
    } catch {
      return null;
    }
  };

  const getAudioTrack = async ({ micId }) => {
    try {
      const track = await createMicrophoneAudioTrack({ microphoneId: micId });
      return track.getAudioTracks()[0];
    } catch {
      return null;
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (videoTrack && videoRef.current) {
      const stream = new MediaStream([videoTrack]);
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [videoTrack]);


  
  // useEffect(() => {
  //   let analyser, source, raf;
  //   if (audioTrack) {
  //     const audioCtx = new AudioContext();
  //     const stream = new MediaStream([audioTrack]);
  //     source = audioCtx.createMediaStreamSource(stream);
  //     analyser = audioCtx.createAnalyser();

  //     // analyser.fftSize = 256;

  //   analyser.fftSize = 512;
  //   analyser.minDecibels = -127;
  //   analyser.maxDecibels = 0;
  //   analyser.smoothingTimeConstant = 0.4;


  //     source.connect(analyser);
  //     const data = new Uint8Array(analyser.frequencyBinCount);
  //     const update = () => {
  //       analyser.getByteFrequencyData(data);
  //       const sum = data.reduce((a, b) => a + b, 0);
  //       setMicLevel(sum / data.length);
  //       raf = requestAnimationFrame(update);
  //     };
  //     update();
  //   }
  //   return () => {
  //     if (raf) cancelAnimationFrame(raf);
  //     if (analyser) analyser.disconnect();
  //     if (source) source.disconnect();
  //   };
  // }, [audioTrack]);

useEffect(() => {
  if (!audioTrack) return;

  let analyser, source, raf, audioCtx;

  audioCtx = new AudioContext();
  const stream = new MediaStream([audioTrack]);
  source = audioCtx.createMediaStreamSource(stream);

  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 512;
  analyser.minDecibels = -127;
  analyser.maxDecibels = 0;
  analyser.smoothingTimeConstant = 0.05; // super quick drop

  source.connect(analyser);

  const data = new Uint8Array(analyser.fftSize);

  const update = () => {
    analyser.getByteTimeDomainData(data);

    // RMS calculation
    let sumSquares = 0;
    for (let i = 0; i < data.length; i++) {
      const value = (data[i] - 128) / 128;
      sumSquares += value * value;
    }
    const rms = Math.sqrt(sumSquares / data.length);

    // Convert RMS → percentage
    let level = Math.min(100, rms * 750);

    // Noise threshold
    if (level < 5) level = 0;

    // Fast fall-off
    setMicLevel(prev => (level < prev ? Math.max(level, prev - 50) : level));

    raf = requestAnimationFrame(update);
  };

  update();

  return () => {
    if (raf) cancelAnimationFrame(raf);
    if (analyser) analyser.disconnect();
    if (source) source.disconnect();
    if (audioCtx) audioCtx.close();
  };
}, [audioTrack]);



// TEST MICROPHONE
const [isRecordingTest, setIsRecordingTest] = useState(false);
const [testAudioUrl, setTestAudioUrl] = useState(null);
const recorderRef = useRef(null);



const testMicrophone = () => {
  if (!audioTrack) {
    alert("No microphone selected!");
    return;
  }

  // If already recording, stop
  if (isRecordingTest && recorderRef.current) {
    recorderRef.current.stop();
    return;
  }

  setIsRecordingTest(true);
  setTestAudioUrl(null);

  const stream = new MediaStream([audioTrack]);
  const recorder = new MediaRecorder(stream);
  recorderRef.current = recorder;
  const chunks = [];

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  recorder.onstop = () => {
    const blob = new Blob(chunks, { type: "audio/webm" });
    const url = URL.createObjectURL(blob);
    setTestAudioUrl(url);
    setIsRecordingTest(false);

    // Auto-play after recording stops
    const audio = new Audio(url);
    audio.play();
  };

  recorder.start();
};


  const [permissionWarning, setPermissionWarning] = useState(false);

  const init = async () => {
  const perms = await checkPermissions();
  const camAllowed = perms.get(Constants.permission.VIDEO);
  const micAllowed = perms.get(Constants.permission.AUDIO);

  // Request if not granted
   if (!micAllowed || !camAllowed) {
    setPermissionWarning(true);
    await requestPermission(Constants.permission.AUDIO);
    await requestPermission(Constants.permission.VIDEO);
  } else {
    setPermissionWarning(false);
  }

  await loadDevices();

  const mics = await getMicrophones();
  const cams = await getCameras();

  // Set default if not already selected
  if (!selectedMic && mics[0]) setSelectedMic(mics[0].deviceId);
  if (!selectedWebcam && cams[0]) setSelectedWebcam(cams[0].deviceId);

  // Auto-enable camera and mic
  if (selectedMic || mics[0]) {
    const mic = await getAudioTrack({ micId: selectedMic || mics[0].deviceId });
    setAudioTrack(mic);
  }
  if (selectedWebcam || cams[0]) {
    const cam = await getVideoTrack({ webcamId: selectedWebcam || cams[0].deviceId });
    setVideoTrack(cam);
  }
};


  const loadDevices = async () => {
    const cams = await getCameras();
    const ms = await getMicrophones();
    const spk = await getPlaybackDevices();
    setWebcams(cams);
    setMics(ms);
    setSpeakers(spk);
    if (cams[0]) setSelectedWebcam(cams[0].deviceId);
    if (ms[0]) setSelectedMic(ms[0].deviceId);
    if (spk[0]) setSelectedSpeaker(spk[0].deviceId);
  };

  const handleDeviceChange = async (type, id) => {
    if (type === "mic") {
      setSelectedMic(id);
      if (audioTrack) audioTrack.stop();
      const mic = await getAudioTrack({ micId: id });
      setAudioTrack(mic);
    }
    if (type === "webcam") {
      setSelectedWebcam(id);
      if (videoTrack) videoTrack.stop();
      const cam = await getVideoTrack({ webcamId: id });
      setVideoTrack(cam);
    }
    if (type === "speaker") {
      setSelectedSpeaker(id);
    }
  };

  const toggleMic = () => {
    if (audioTrack) {
      audioTrack.stop();
      setAudioTrack(null);
    } else {
      getAudioTrack({ micId: selectedMic }).then(setAudioTrack);
    }
  };

  const toggleCamera = () => {
    if (videoTrack) {
      videoTrack.stop();
      setVideoTrack(null);
    } else {
      getVideoTrack({ webcamId: selectedWebcam }).then(setVideoTrack);
    }
  };

  const testSpeaker = () => {
    if (speakerTesting) return;
    setSpeakerTesting(true);
    const audio = new Audio("https://actions.google.com/sounds/v1/alarms/bugle_tune.ogg");
    audio.setSinkId && audio.setSinkId(selectedSpeaker);
    audio.play().finally(() => setTimeout(() => setSpeakerTesting(false), 5000));
  };

  // Network Stats
  const [error, setError] = useState("no-error-loading");
  const [uploadSpeed, setUploadSpeed] = useState(null);
  const [downloadSpeed, setDownloadSpeed] = useState(null);

  const getNetworkStatistics = async () => {
    setError("no-error-loading");
    try {
      const options = { timeoutDuration: 45000 };
      const networkStats = await getNetworkStats(options);
      setDownloadSpeed(networkStats.downloadSpeed);
      setUploadSpeed(networkStats.uploadSpeed);
      setError("no-error");
    } catch (ex) {
      if (ex === "Not able to get NetworkStats due to no Network") setError("no-wifi");
      if (ex === "Not able to get NetworkStats due to timeout") setError("timeout");
    }
  };

  useEffect(() => {
    getNetworkStatistics();
  }, []);


const [selectedMicLabel, setSelectedMicLabel] = useState("");
const [selectedSpeakerLabel, setSelectedSpeakerLabel] = useState("");
const [selectedCamLabel, setSelectedCamLabel] = useState("");

// PRE SELECT MIC
useEffect(() => {
  if (mics.length > 0 && !selectedMicLabel) {
    const firstMic = mics[0];
    setSelectedMic(firstMic.deviceId);
    handleMicSelect(firstMic.deviceId, firstMic.label);
  }
}, [mics, selectedMic]);


const handleMicSelect = (deviceId, label) => {
  setSelectedMicLabel(label || "Mic");
  handleDeviceChange("mic", deviceId);
};


// PRE SELECT SPEAKER
useEffect(() => {
  if (speakers.length > 0 && !selectedSpeakerLabel) {
    const first = speakers[0];
    setSelectedSpeaker(first.deviceId);
    handleSpeakerSelect(first.deviceId, first.label);
  }
}, [speakers, selectedSpeaker]);


const handleSpeakerSelect = (deviceId, label) => {
  setSelectedSpeakerLabel(label || "Speaker");
  handleDeviceChange("speaker", deviceId);
};






// PRE SELECT WEBCAM
useEffect(() => {
  if (webcams.length > 0 && !selectedCamLabel) {
    const first = webcams[0];
    setSelectedWebcam(first.deviceId);
    handleCamSelect(first.deviceId, first.label);
  }
}, [webcams, selectedWebcam]);

const handleCamSelect = (deviceId, label) => {
  setSelectedCamLabel(label || "Webcam");
  handleDeviceChange("webcam", deviceId);
};


const [isPlayingTest, setIsPlayingTest] = useState(false);
const audioRef = useRef(null);

  return (
    <div className="d-flex  flex-1 " style={{position:"fixed",inset:0,backgroundColor: "rgba(5, 10, 14, 1)"}}>
      <div className="d-flex flex-1 flex-column 100vh " style={{overflowY:"auto"}}>
        <div className="d-flex flex-1 flex-column flex-md-row align-items-center justify-content-center">
          <div className="container">



            <div className="row">
              <div className="col-12 col-md-12 col-lg-7 d-flex align-items-center flex-column">




                <div className="position-relative rounded overflow-hidden" style={{ width: '100%', height: '55vh', background: '#000' }} >








                  {videoTrack ?
                    <video ref={videoRef} autoPlay muted playsInline className="w-100 h-100"

                      style={{
                        objectFit: 'cover',
                        transform: 'scaleX(-1)', // 🔁 Mirror the video horizontally
                      }}/>


                  :
                    <div className="position-absolute top-0 bottom-0 left-0 right-0 d-flex align-items-center justify-content-center w-100" >
                      <div className="container text-center  ">
                        <p className="text-white fs-3">The camera is off</p>
                      </div>
                    </div>
                  }





                  {/* Network Speed Test (Top Right) */}
                  <div className="position-absolute d-flex flex-row top-0 end-0 m-2 p-2 rounded   text-white small" style={{ minWidth: '150px' }} >

                    <div className="btn-group bg-opacity-75" role="group" aria-label="First group" style={{ color: "white", backgroundColor: "rgba(5, 10, 14, 1)" }} >
                      {(() => {
                        if (error === "no-error-loading") {
                          return (
                            <button type="button" className="btn btn-outline-secondary" onClick={getNetworkStatistics} >
                              <p className="mb-0">
                                Checking network speeds{" "}
                                <i className="tf-icons bx bx-refresh spin" />
                              </p>
                            </button>
                          );
                        } else if (error === "no-wifi") {
                          return (
                            <>
                             <div className="btn btn-outline-secondary">
                                <p className="text-danger mb-0 ms-2">Offline</p>
                              </div>

                              <button type="button" className="btn btn-outline-secondary" onClick={getNetworkStatistics} >
                                <i className="tf-icons bx bx-rotate-right" />
                              </button>
                            </>
                         
                          );
                        } else if (error === "timeout") {
                          return (
                            <>
                              <div className="btn btn-outline-secondary">
                                <p className="text-danger mb-0 ms-2">Timeout</p>
                              </div>
                              <button type="button" className="btn btn-outline-secondary" onClick={getNetworkStatistics} >
                                <i className="tf-icons bx bx-rotate-right" />
                              </button>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <div className="btn btn-outline-secondary p-1">
                                <i className="tf-icons bx bx-down-arrow-alt" />
                                {downloadSpeed} MBPS
                              </div>
                              <div className="btn btn-outline-secondary p-1">
                                <i className="tf-icons bx bx-up-arrow-alt" />
                                {uploadSpeed} MBPS
                              </div>
                              <button type="button" className="btn btn-outline-secondary p-1" onClick={getNetworkStatistics} >
                                <i className="tf-icons bx bx-rotate-right" />
                              </button>
                            </>
                          );
                        }
                      })()}
                    </div>


                 

               
                    
          
                   
                  </div>



                  {/* MIC AND CAMERA */}
                  <div className="position-absolute d-flex justify-content-center gap-3 bottom-0 m-2 p-2   w-100 text-center" >
                    <Tooltip title="Toggle Mic" slotProps={{ tooltip: { sx: { fontSize: '1.15em' } } }}>
                      <button type="button" className="btn  p-2  rounded-circle" style={{ width: 48, height: 48, color: "black",background:audioTrack ? "white" : "#f36565" }} onClick={() => toggleMic()}><i className={`tf-icons fs-3 mt-1 bx ${audioTrack ? "bxs-microphone" : "bxs-microphone-off  text-white"}`} style={{ fontSize: "large" }}></i></button>
                    </Tooltip>

                    <Tooltip title="Toggle Camera" slotProps={{ tooltip: { sx: { fontSize: '1.15em' } } }}>
                      <button type="button" className="btn    rounded-circle p-2 " style={{ width: 48, height: 48, color: "black",background:videoTrack ? "white" : "#f36565" }} onClick={() => toggleCamera()} ><i className={`tf-icons fs-3 mt-1 bx ${videoTrack ? "bxs-video" : "bxs-video-off text-white"}`} style={{ fontSize: "large" }}></i></button>
                    </Tooltip>
                  </div>
                </div>


 

                <div className="w-100 p-2  bg-opacity-75 d-flex flex-row justify-content-center justify-content-lg-start"> 
                  <div className="btn-group mb-2">
                    {/* MICROHPONE */}
                    <>
                      <button type="button" className="rounded-0 rounded-start btn btn-sm btn-outline-secondary dropdown-toggle d-none d-lg-block d-md-block"

                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-auto-close="outside"

                        style={{
                          width: '175px',
                          textAlign: 'left',
                          paddingRight: '1.5rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}>
                        <i className="tf-icons bx bxs-microphone me-1" />
                        {selectedMicLabel}
                      </button>

                    <button type="button" style={{width:125}} className="rounded-0 rounded-start btn btn-sm btn-outline-secondary dropdown-toggle p-2 p-sm-2 px-lg-5 px-md-4 py-lg-4 d-block d-lg-none d-md-none"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      >
                      <i className="tf-icons bx bxs-microphone me-1"  style={{ fontSize: "xxx-large" }} />
                    </button>

                      <div className="dropdown-menu p-0 bg-dark shadow-none" style={{ maxWidth: '290px', whiteSpace: 'normal', wordBreak: 'break-word', color: "white" }} >
                        {mics.map((m) => (
                          <div key={m.deviceId} className="dropdown-item" onClick={() => handleMicSelect(m.deviceId, m.label)} style={{ cursor: 'pointer', whiteSpace: 'normal', color: "white" }}>
                            {m.label || "Mic"}
                            {selectedMic === m.deviceId && (
                              <i className="bx bx-check ms-2 fs-3" />
                            )}
                          </div>
                        ))}

                        <hr className="dropdown-divider"></hr>

                        <div className="d-flex flex-row justify-content-around my-1 align-items-center">
                          <i className="tf-icons bx bxs-microphone me-1" />
                          {audioTrack && (
                            <div className="progress" style={{ width: 120, backgroundColor: "rgba(5, 10, 14, 1)" }} >
                              <div
                                className="progress-bar bg-white"
                                role="progressbar"
                                style={{
                                  width: `${Math.min(100, micLevel)}%`,
                                  color: "black",
                                }}
                              />
                            </div>
                          )}

                          {/* Record / Stop Button */}
                          <button className={`btn btn-sm ${isRecordingTest ? "btn-danger" : "btn-outline-light"}`} onClick={testMicrophone} >
                            {isRecordingTest ? "Stop" : "Record"}
                          </button>

                          {/* Play Button (only if we have recorded audio) */}
                          {testAudioUrl && !isRecordingTest && (
                            <button
                              className={`btn btn-sm ${isPlayingTest ? "btn-danger" : "btn-success"}`}
                              onClick={() => {
                                if (!audioRef.current) {
                                  audioRef.current = new Audio(testAudioUrl);
                                  audioRef.current.onended = () => setIsPlayingTest(false);
                                }

                                if (isPlayingTest) {
                                  audioRef.current.pause();
                                  audioRef.current.currentTime = 0; // reset to start
                                  setIsPlayingTest(false);
                                } else {
                                  audioRef.current.play();
                                  setIsPlayingTest(true);
                                }
                              }}
                            >
                              {isPlayingTest ? "⏹ Stop" : "▶ Play"}
                            </button>
                          )}

                        </div>
                      </div>
                    </>

                    {/* SPEAKER */}
                    <>
                      <button type="button" className="rounded-0 btn btn-sm btn-outline-secondary dropdown-toggle  d-none d-lg-block d-md-block"

                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-auto-close="outside"

                        style={{
                          width: '175px',
                          textAlign: 'left',
                          paddingRight: '1.5rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}
                      >
                        <i className="tf-icons bx bxs-volume-full me-1" />
                        <span>{selectedSpeakerLabel}</span>
                      </button>

                      <button type="button" style={{width:125}} className="rounded-0 btn btn-sm btn-outline-secondary dropdown-toggle p-2 p-sm-2 px-lg-5 px-md-4 py-lg-4 d-block d-lg-none d-md-none"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      >
                      <i className="tf-icons bx bxs-volume-full me-1"  style={{ fontSize: "xxx-large" }} />
                    </button>



                      <div className="dropdown-menu p-0 bg-dark shadow-none" style={{ maxWidth: '290px', whiteSpace: 'normal', wordBreak: 'break-word', color: "white" }} >
                        {speakers.map((m) => (
                          <div key={m.deviceId} className="dropdown-item" onClick={() => handleSpeakerSelect(m.deviceId, m.label)} style={{ cursor: 'pointer', whiteSpace: 'normal', color: "white" }}>
                            {m.label || "Mic"}
                            {selectedSpeaker === m.deviceId && (
                              <i className="bx bx-check ms-2 fs-3" />
                            )}
                          </div>
                        ))}

                        <hr className="dropdown-divider"></hr>

                        <div className="d-flex flex-row justify-content-around my-1 align-items-center">
                          <i className="tf-icons bx bxs-volume-full me-1" />
                          <button className="btn btn-outline-light btn-sm" onClick={testSpeaker} disabled={speakerTesting} >
                            {speakerTesting ? "Testing Speaker..." : "▶ Test Speaker"}
                          </button>
                        </div>
                      </div>
                      </>



                    {/* CAMERA */}
                      <button type="button" className="rounded-0 rounded-end btn btn-sm btn-outline-secondary dropdown-toggle  d-none d-lg-block d-md-block"

                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-auto-close="outside"

                        style={{
                          width: '175px',
                          textAlign: 'left',
                          paddingRight: '1.5rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}
                      >
                        <i className="tf-icons bx bxs-video me-1" />
                        {selectedCamLabel}
                      </button>

                       <button type="button"  style={{width:125}} className="rounded-0 rounded-end btn btn-sm btn-outline-secondary dropdown-toggle p-2 p-sm-2 px-lg-5 px-md-4 py-lg-4 d-block d-lg-none d-md-none"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        >
                        <i className="tf-icons bx bxs-video me-1"  style={{ fontSize: "xxx-large" }} />
                      </button>

                      <div className="dropdown-menu p-0 bg-dark shadow-none" style={{ maxWidth: '290px', whiteSpace: 'normal', wordBreak: 'break-word', color: "white" }} >
                        {webcams.map((m) => (
                          <div key={m.deviceId} className="dropdown-item" onClick={() => handleCamSelect(m.deviceId, m.label)} style={{ cursor: 'pointer', whiteSpace: 'normal', color: "white" }}>
                            {m.label || "Camera"}
                            {selectedWebcam === m.deviceId && (
                              <i className="bx bx-check ms-2 fs-3" />
                            )}
                          </div>
                        ))}
                      </div>

                  </div>



                </div>
               
 





            </div>

              <div className="col-12 col-md-12 col-lg-5 d-flex align-items-center">

                <div className="container text-center">
                  <div className="mt-4">
                    {!meetingId ? (
                      <>
                        <input
                          type="text"
                          className="form-control mb-2"
                          placeholder="Enter Meeting ID"
                          onChange={(e) => setInputId(e.target.value)}
                        />
                        <button className="btn btn-primary w-100 mb-2" onClick={() => getMeetingAndToken(inputId)}>
                          Join Meeting
                        </button>
                        <button className="btn btn-secondary w-100" onClick={() => getMeetingAndToken(null)}>
                          Create New Meeting
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-success fs-3 fw-bold">
                          Meeting Code: <strong>{meetingId}</strong>
                        </p>
                        <button
                          className="btn btn-success w-100"
                          onClick={() =>
                            onStartMeeting({
                              micEnabled: !!audioTrack,
                              webcamEnabled: !!videoTrack,
                              customMicrophoneAudioTrack: audioTrack instanceof MediaStreamTrack ? new MediaStream([audioTrack]) : undefined,
                              customCameraVideoTrack: videoTrack instanceof MediaStreamTrack ? new MediaStream([videoTrack]) : undefined,
                              microphoneId: selectedMic,
                              webcamId: selectedWebcam,
                            })
                          }
                        >
                          Join Meeting
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>










          </div>













        </div>
      </div>

      {permissionWarning && (

        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
          <span className="fw-medium">Please allow camera and microphone access from your browser settings.</span>
            <button className="btn btn-sm btn-outline-primary ms-2" onClick={init}>Retry</button>
        </div>
      )}


    </div>
  );
}
